import React, {useEffect, useRef, useState} from 'react';
import PaymentAccessBlockedDialog from './PaymentAccessBlockedDialog';
import {
  getPaymentBlockedDetail,
  PAYMENT_ACCESS_BLOCKED_CODE,
  PAYMENT_ACCESS_BLOCKED_MESSAGE,
  PAYMENT_ACCESS_BLOCKED_TITLE,
} from './paymentStatus';


const publicBlockedDetail = {
  code: PAYMENT_ACCESS_BLOCKED_CODE,
  title: PAYMENT_ACCESS_BLOCKED_TITLE,
  message: PAYMENT_ACCESS_BLOCKED_MESSAGE,
};

export const DepartmentSelectorCard = ({
  department,
  onSelect,
  onBlocked,
  disabled = false,
}) => {
  const blocked = department.access_blocked === true;
  const handleClick = () => {
    if (disabled) return;
    if (blocked) return onBlocked(department);
    return onSelect(department);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={`rounded-xl border-2 p-4 sm:p-6 md:p-8 xl:p-10 flex min-h-[120px] w-full flex-col items-center justify-center transition-all duration-300 sm:min-h-[140px] md:min-h-[160px] xl:min-h-[180px] ${
        disabled
          ? 'cursor-not-allowed border-transparent bg-white opacity-40 shadow-lg'
          : blocked
            ? 'cursor-pointer border-red-400 bg-slate-200 shadow-lg hover:bg-slate-300 hover:shadow-xl'
            : 'cursor-pointer border-transparent bg-white shadow-lg hover:scale-105 hover:shadow-xl'
      }`}
    >
      <span className={`px-2 text-center text-lg font-semibold leading-tight sm:text-xl md:text-2xl xl:text-3xl ${
        blocked ? 'text-slate-700' : 'text-blue-800'
      }`}>
        {department.name}
      </span>
      {blocked && (
        <span className="mt-3 rounded-full border border-red-400 bg-red-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-red-800">
          Deaktiviert
        </span>
      )}
    </button>
  );
};

const BlockedDepartmentAccessFlow = ({
  department,
  onClose,
  onAdminLoginSuccess,
  verifyAdmin,
}) => {
  const [phase, setPhase] = useState('notice');
  const [detail, setDetail] = useState(publicBlockedDetail);
  const [adminVerified, setAdminVerified] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const verificationAttempt = useRef(0);
  const mounted = useRef(true);

  useEffect(() => () => {
    mounted.current = false;
    verificationAttempt.current += 1;
  }, []);

  const handleAdminSubmit = async event => {
    event.preventDefault();
    const attempt = verificationAttempt.current + 1;
    verificationAttempt.current = attempt;
    setError('');
    setSubmitting(true);
    try {
      const response = await verifyAdmin(department, password);
      if (!mounted.current || verificationAttempt.current !== attempt) return;
      onAdminLoginSuccess(response.data);
    } catch (requestError) {
      if (!mounted.current || verificationAttempt.current !== attempt) return;
      const blockedDetail = getPaymentBlockedDetail(requestError);
      if (blockedDetail) {
        setDetail(blockedDetail);
        setAdminVerified(true);
        setPhase('notice');
      } else {
        setError('Ungültiges Admin-Passwort');
      }
    } finally {
      if (mounted.current && verificationAttempt.current === attempt) {
        setSubmitting(false);
        setPassword('');
      }
    }
  };

  const returnToNotice = () => {
    verificationAttempt.current += 1;
    setSubmitting(false);
    setPassword('');
    setError('');
    setPhase('notice');
  };

  if (phase === 'notice') {
    return (
      <PaymentAccessBlockedDialog
        detail={detail}
        onAdminAccess={adminVerified ? undefined : () => setPhase('admin')}
        onClose={onClose}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/60 p-4 py-10"
      role="dialog"
      aria-modal="true"
      aria-labelledby="blocked-admin-login-title"
    >
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl sm:p-8">
        <h2 id="blocked-admin-login-title" className="text-xl font-bold text-slate-900">
          Zahlungsstatus für {department.name}
        </h2>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          Die Payment-Zugangsdaten werden erst nach erfolgreicher Prüfung des
          Admin-Passworts angezeigt.
        </p>
        <form onSubmit={handleAdminSubmit} className="mt-6">
          <label className="block text-sm font-semibold text-slate-800">
            Admin-Passwort
            <input
              type="password"
              value={password}
              onChange={event => setPassword(event.target.value)}
              autoComplete="current-password"
              autoFocus
              className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 font-normal focus:border-blue-500 focus:outline-none"
              required
            />
          </label>
          {error && (
            <p className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </p>
          )}
          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={returnToNotice}
              className="flex-1 rounded-lg bg-slate-200 px-4 py-2.5 font-medium text-slate-800 hover:bg-slate-300"
            >
              Zurück
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 rounded-lg bg-blue-700 px-4 py-2.5 font-medium text-white hover:bg-blue-800 disabled:opacity-60"
            >
              {submitting ? 'Prüfen …' : 'Prüfen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlockedDepartmentAccessFlow;
